/**
 * Fee preview (matches backend `computeCharge` in portal donate / pledge services).
 * Card/wallet: processing fee is always `pct`% of the donation base. ACH: no fee.
 * Build-time: REACT_APP_CREDIT_CARD_FEE_PERCENTAGE. Runtime: merge `feeUi` from GET /public/donate/fee-ui or payment-intent responses.
 */

function stripQuotedEnv(s) {
  let v = String(s ?? '').trim();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    v = v.slice(1, -1).trim();
  }
  return v;
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

const pct = Number(process.env.REACT_APP_CREDIT_CARD_FEE_PERCENTAGE);

/** Match backend `CREDIT_CARD_FEE_NAME` / `CREDIT_CARD_FEE_CAPTION` when REACT_APP_* unset (fee-ui merge still preferred at runtime). */
const DEFAULT_CREDIT_CARD_FEE_NAME = 'Credit card processing fee (2.5%).';
const DEFAULT_CREDIT_CARD_FEE_CAPTION =
  'Please consider covering transaction fees so 100% of your valuable donation goes towards the temple.';

export const donationFeeEnv = {
  pct: Number.isFinite(pct) && pct >= 0 ? pct : 2.5,
  feeName: stripQuotedEnv(process.env.REACT_APP_CREDIT_CARD_FEE_NAME) || DEFAULT_CREDIT_CARD_FEE_NAME,
  feeCaption: stripQuotedEnv(process.env.REACT_APP_CREDIT_CARD_FEE_CAPTION) || DEFAULT_CREDIT_CARD_FEE_CAPTION,
};

/**
 * Merge server `feeUi` object(s) over build-time defaults. Later sources win.
 * @param {...Record<string, unknown>|null|undefined} sources
 */
export function mergedFeeDisplay(...sources) {
  const out = { ...donationFeeEnv };
  for (const s of sources) {
    if (!s || typeof s !== 'object') continue;
    if (Number.isFinite(Number(s.pct)) && Number(s.pct) >= 0) out.pct = Number(s.pct);
    if (typeof s.feeName === 'string' && s.feeName.trim()) out.feeName = s.feeName.trim();
    if (typeof s.feeCaption === 'string' && s.feeCaption.trim()) out.feeCaption = s.feeCaption.trim();
  }
  return out;
}

export function computeClientFeeTotal(base, fundingType, feeDisplay = donationFeeEnv) {
  const b = round2(base);
  if (fundingType === 'ach') return { base: b, fee: 0, total: b };
  const pctVal = feeDisplay.pct ?? 2.5;
  const fee = round2((b * pctVal) / 100);
  return { base: b, fee, total: round2(b + fee) };
}

/** Short phrase for radio labels (keeps line short; full wording is in feeCaption / feeName). */
export function cardFeeRadioSuffix(feeDisplay = donationFeeEnv) {
  const p = feeDisplay.pct ?? 2.5;
  return `${p}%`;
}
